import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wrench, ClipboardCheck, FileText, BookOpen, Bell,
  Receipt, CalendarDays, FolderOpen,
  BookMarked, AlertTriangle, CloudUpload, ChevronRight, Menu, Phone, Settings,
  FileEdit, Trash2, X, Package,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useOfflineQueue } from '../hooks/useOfflineQueue';
import api from '../utils/api';
import { getDraftCount, getDrafts, deleteDraft } from '../hooks/useDrafts';
import TurnoEmergencia from '../components/TurnoEmergencia';

export default function Home() {
  const { tecnico, logout } = useAuth();
  const navigate = useNavigate();
  const [driveUrl, setDriveUrl]   = useState(null);
  const [showMore, setShowMore]   = useState(false);

  const esAdmin        = ['admin', 'superadmin'].includes(tecnico?.rol);
  const esCentral      = tecnico?.rol === 'central';
  const esTecnico      = tecnico?.rol === 'tecnico';
  const esPresupuestos = tecnico?.rol === 'presupuestos';
  const esFacturacion  = tecnico?.rol === 'facturacion';
  const esRiesgos      = tecnico?.rol === 'riesgos';

  const { total: pendientesTotal } = useOfflineQueue();
  const [numBorradores, setNumBorradores] = useState(0);
  const [verBorradores, setVerBorradores] = useState(false);
  const [listaBorradores, setListaBorradores] = useState([]);

  const recargarBorradores = () => {
    if (!tecnico?.id) return;
    const lista = getDrafts(tecnico.id);
    setListaBorradores(lista);
    setNumBorradores(lista.length);
  };

  useEffect(() => {
    if (esAdmin) {
      api.get('/admin/drive-url').then(r => setDriveUrl(r.data.url)).catch(() => {});
    }
  }, [esAdmin]);

  useEffect(() => {
    recargarBorradores();
  }, [tecnico?.id]);

  /* ── Sub-componentes ── */

  const FormCard = ({ icon: Icon, title, sub, onClick, accentColor }) => (
    <div onClick={onClick} className="form-card" style={{
      flex: 1, borderRadius: 16, padding: '20px 14px',
      border: `1px solid ${accentColor}40`, cursor: 'pointer', textAlign: 'center',
    }}>
      <div style={{
        width: 52, height: 52, borderRadius: 14, background: `${accentColor}22`,
        margin: '0 auto 12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={24} color={accentColor} strokeWidth={1.75} />
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#F4F7F5', lineHeight: 1.3 }}>{title}</div>
      <div style={{ fontSize: 11, color: '#8A9994', marginTop: 4 }}>{sub}</div>
    </div>
  );

  const ToolItem = ({ icon: Icon, label, onClick, iconBg, iconColor }) => (
    <div onClick={onClick} className="nav-btn" style={{
      borderRadius: 12, padding: '14px 8px',
      border: '1px solid rgba(255,255,255,0.07)',
      textAlign: 'center', cursor: 'pointer',
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10, background: iconBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px',
      }}>
        <Icon size={16} color={iconColor} strokeWidth={2} />
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#C9D3CE', lineHeight: 1.3 }}>{label}</div>
    </div>
  );

  const SectionLabel = ({ text }) => (
    <div style={{
      fontSize: 11, fontWeight: 700, color: '#6B7C74',
      textTransform: 'uppercase', letterSpacing: '1px',
      marginBottom: 10, marginTop: 22, paddingLeft: 10, borderLeft: '2px solid #2E4A40',
    }}>{text}</div>
  );

  const BorradorBtn = () => (
    <button onClick={() => { recargarBorradores(); setVerBorradores(true); }} style={{
      width: '100%', background: '#1C1200', border: '1px solid #F59E0B', borderRadius: 12,
      padding: '12px 16px', marginBottom: 8, cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
    }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: '#2D1E00',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <FileEdit size={16} color="#F59E0B" strokeWidth={2} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#F4F7F5' }}>
          {numBorradores} borrador{numBorradores > 1 ? 'es' : ''} pendiente{numBorradores > 1 ? 's' : ''}
        </div>
        <div style={{ fontSize: 12, color: '#F59E0B', marginTop: 2 }}>Expiran en 24h — toca para ver</div>
      </div>
      <ChevronRight size={16} color="#F59E0B" strokeWidth={2} />
    </button>
  );

  const RendicionBtn = () => (
    <div onClick={() => navigate('/rendiciones')} className="nav-btn" style={{
      display: 'flex', alignItems: 'center', gap: 14,
      borderRadius: 14, padding: '13px 16px', marginBottom: 8,
      border: '1px solid #3D2B00', cursor: 'pointer', background: '#1C1200',
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, background: '#2D1E00',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Receipt size={18} color="#F59E0B" strokeWidth={2} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#F4F7F5' }}>Rendición de gastos</div>
        <div style={{ fontSize: 12, color: '#F59E0B', marginTop: 2 }}>Gestiona los gastos del período</div>
      </div>
      <ChevronRight size={16} color="#4D6358" strokeWidth={2} />
    </div>
  );

  const InsumosBtn = () => (
    <div onClick={() => navigate('/insumos')} className="nav-btn" style={{
      display: 'flex', alignItems: 'center', gap: 14,
      borderRadius: 14, padding: '13px 16px', marginBottom: 8,
      border: '1px solid #12382D', cursor: 'pointer', background: '#0C1512',
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, background: '#12382D',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Package size={18} color="#4CAF50" strokeWidth={2} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#F4F7F5' }}>Solicitar insumos</div>
        <div style={{ fontSize: 12, color: '#4CAF50', marginTop: 2 }}>Retira material de bodega</div>
      </div>
      <ChevronRight size={16} color="#4D6358" strokeWidth={2} />
    </div>
  );

  const PendientesBanner = () => (
    <div onClick={() => navigate('/pendientes')} style={{
      display: 'flex', alignItems: 'center', gap: 14,
      borderRadius: 14, padding: '13px 16px', marginBottom: 8,
      border: '1px solid #3D2B00', cursor: 'pointer', background: '#1C1200',
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, background: '#2D1E00',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <CloudUpload size={18} color="#F59E0B" strokeWidth={2} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#F4F7F5' }}>Pendientes de envío</div>
        <div style={{ fontSize: 12, color: '#F59E0B', marginTop: 2 }}>
          {pendientesTotal} formulario{pendientesTotal !== 1 ? 's' : ''} sin conexión
        </div>
      </div>
      <span style={{
        background: '#e65100', color: 'white', borderRadius: 20,
        padding: '2px 9px', fontSize: 12, fontWeight: 700,
      }}>{pendientesTotal}</span>
    </div>
  );

  const EmergenciaBtn = () => (
    <div onClick={() => navigate('/admin?tab=notificaciones')} className="nav-btn" style={{
      display: 'flex', alignItems: 'center', gap: 14,
      borderRadius: 14, padding: '13px 16px', marginBottom: 8,
      border: '1px solid #3D1515', cursor: 'pointer', background: '#1A0808',
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, background: '#2D0F0F',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <AlertTriangle size={18} color="#EF4444" strokeWidth={2} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#F4F7F5' }}>Enviar alerta de emergencia</div>
        <div style={{ fontSize: 12, color: '#EF4444', marginTop: 2 }}>Notifica al equipo técnico</div>
      </div>
      <ChevronRight size={16} color="#4D6358" strokeWidth={2} />
    </div>
  );

  /* ── Herramientas secundarias por rol ── */
  const tecnicoTools = [
    { icon: FileText,     label: 'Historial',    iconBg: '#12382D', iconColor: '#4CAF50', path: '/historial' },
    { icon: BookOpen,     label: 'Bitácoras',    iconBg: '#0D1E2E', iconColor: '#60A5FA', path: '/bitacoras' },
    { icon: CalendarDays, label: 'Planificación',iconBg: '#1C1228', iconColor: '#A78BFA', path: '/planificacion' },
  ];

  const adminTools = [
    { icon: FileText,    label: 'Historial',  iconBg: '#12382D', iconColor: '#4CAF50', path: '/historial' },
    { icon: BookOpen,    label: 'Bitácoras',  iconBg: '#0D1E2E', iconColor: '#60A5FA', path: '/bitacoras' },
    { icon: CalendarDays,label: 'Planificación',iconBg: '#1C1228', iconColor: '#A78BFA', path: '/planificacion' },
    ...(driveUrl ? [{ icon: FolderOpen, label: 'Drive', iconBg: '#12382D', iconColor: '#4CAF50', url: driveUrl }] : []),
  ];

  return (
    <div className="app-container">

      {/* ── Header ── */}
      <div style={{
        background: esCentral ? '#0D0D1A' : '#0B0F0D',
        padding: '14px 16px',
        display: 'flex', alignItems: 'center', gap: 8,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        {/* Hamburguesa — despliega más herramientas */}
        <button onClick={() => setShowMore(v => !v)} style={{
          background: showMore ? 'rgba(15,110,86,0.15)' : 'none',
          border: showMore ? '1px solid rgba(15,110,86,0.3)' : '1px solid transparent',
          cursor: 'pointer', padding: 7, color: showMore ? '#4CAF50' : '#8A9994',
          display: 'flex', alignItems: 'center', borderRadius: 8, flexShrink: 0,
        }}>
          <Menu size={20} strokeWidth={1.75} />
        </button>

        {/* Texto bienvenida */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 11, color: '#6B7C74', letterSpacing: '0.3px' }}>
            {esCentral ? 'Central de emergencias' : 'Bienvenido'}
          </p>
          <h1 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#F4F7F5',
                       overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {tecnico?.nombre}
          </h1>
        </div>

        {/* Directorio — teléfono */}
        {(esAdmin || esTecnico || esRiesgos) && (
          <button onClick={() => navigate('/directorio')} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 7, color: '#8A9994', display: 'flex', alignItems: 'center',
            borderRadius: 8, flexShrink: 0,
          }}>
            <Phone size={19} strokeWidth={1.75} />
          </button>
        )}

        {/* Administración — engranaje */}
        {esAdmin && (
          <button onClick={() => navigate('/admin')} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 7, color: '#8A9994', display: 'flex', alignItems: 'center',
            borderRadius: 8, flexShrink: 0,
          }}>
            <Settings size={19} strokeWidth={1.75} />
          </button>
        )}

        {/* Notificaciones — campana */}
        <button onClick={() => navigate('/notificaciones')} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          padding: 7, color: '#8A9994', display: 'flex', alignItems: 'center',
          borderRadius: 8, flexShrink: 0,
        }}>
          <Bell size={20} strokeWidth={1.75} />
        </button>

        <button onClick={logout} style={{
          background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.10)',
          color: '#9FAAA5', borderRadius: 10, padding: '6px 13px',
          fontSize: 13, fontWeight: 500, cursor: 'pointer', flexShrink: 0,
        }}>Salir</button>
      </div>

      <div className="page-content">

        {/* ── CENTRAL ── */}
        {esCentral && (
          <>
            <SectionLabel text="Turno de emergencias" />
            <TurnoEmergencia />

            <SectionLabel text="Acciones" />
            <EmergenciaBtn />
          </>
        )}

        {/* ── TÉCNICO ── */}
        {esTecnico && (
          <>
            <SectionLabel text="Formularios" />
            <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
              <FormCard icon={Wrench} title="Mantenimiento Preventivo" sub="Checklist mensual"
                accentColor="#0F6E56" onClick={() => navigate('/formulario/mantenimiento')} />
              <FormCard icon={ClipboardCheck} title="Visita Técnica" sub="Intervención o emergencia"
                accentColor="#378add" onClick={() => navigate('/formulario/visita')} />
            </div>

            {numBorradores > 0 && <BorradorBtn />}

            {pendientesTotal > 0 && <PendientesBanner />}
            <InsumosBtn />
            <RendicionBtn />

            {showMore && (
              <>
                <SectionLabel text="Herramientas" />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 8 }}>
                  {tecnicoTools.map(t => (
                    <ToolItem key={t.label} icon={t.icon} label={t.label}
                      iconBg={t.iconBg} iconColor={t.iconColor}
                      onClick={() => navigate(t.path)} />
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* ── PRESUPUESTOS ── */}
        {esPresupuestos && (
          <>
            <SectionLabel text="Gestión" />
            <ToolItem icon={FileText} label="Presupuestos"
              iconBg="#12382D" iconColor="#4CAF50"
              onClick={() => navigate('/admin')} />
          </>
        )}

        {/* ── FACTURACIÓN ── */}
        {esFacturacion && (
          <>
            <SectionLabel text="Gestión" />
            <ToolItem icon={FileText} label="Facturación"
              iconBg="#12382D" iconColor="#4CAF50"
              onClick={() => navigate('/admin?tab=facturable')} />
          </>
        )}

        {/* ── RIESGOS ── */}
        {esRiesgos && (
          <>
            <SectionLabel text="Gestión" />
            <ToolItem icon={FileText} label="Panel de gestión"
              iconBg="#12382D" iconColor="#4CAF50"
              onClick={() => navigate('/admin')} />

            <SectionLabel text="Acciones" />
            <RendicionBtn />
          </>
        )}

        {/* ── ADMIN / SUPERADMIN ── */}
        {esAdmin && (
          <>
            <SectionLabel text="Formularios" />
            <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
              <FormCard icon={Wrench} title="Mantenimiento Preventivo" sub="Checklist mensual"
                accentColor="#0F6E56" onClick={() => navigate('/formulario/mantenimiento')} />
              <FormCard icon={ClipboardCheck} title="Visita Técnica" sub="Intervención o emergencia"
                accentColor="#378add" onClick={() => navigate('/formulario/visita')} />
            </div>

            {numBorradores > 0 && <BorradorBtn />}

            {pendientesTotal > 0 && <PendientesBanner />}

            <SectionLabel text="Acciones rápidas" />
            <EmergenciaBtn />
            <InsumosBtn />
            <RendicionBtn />

            {showMore && (
              <>
                <SectionLabel text="Herramientas" />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 8 }}>
                  {adminTools.map(t => (
                    <ToolItem key={t.label} icon={t.icon} label={t.label}
                      iconBg={t.iconBg} iconColor={t.iconColor}
                      onClick={t.url ? () => window.open(t.url, '_blank') : () => navigate(t.path)} />
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* ── Manuales — todos los roles ── */}
        <div onClick={() => window.open(
            'https://drive.google.com/drive/folders/1-LZYHQ0I7vrTOeGLqpl-i5ZGhLlNhZ2q', '_blank'
          )} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 12px', marginTop: 18, borderRadius: 10,
          border: '1px solid rgba(255,255,255,0.05)', background: '#0D1311', cursor: 'pointer',
        }}>
          <BookMarked size={14} color="#60A5FA" />
          <span style={{ fontSize: 13, color: '#6B7C74', flex: 1 }}>Manuales técnicos</span>
          <ChevronRight size={13} color="#4D6358" />
        </div>

        <div style={{ height: 28 }} />
      </div>

      {/* ── Modal borradores ── */}
      {verBorradores && (
        <div onClick={() => setVerBorradores(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          zIndex: 200, display: 'flex', alignItems: 'flex-end',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#111814', borderRadius: '20px 20px 0 0',
            width: '100%', padding: '20px 16px', maxHeight: '70vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#F4F7F5' }}>Borradores guardados</span>
              <button onClick={() => setVerBorradores(false)} style={{
                background: 'none', border: 'none', color: '#8A9994', cursor: 'pointer', padding: 4,
              }}>
                <X size={20} />
              </button>
            </div>

            {listaBorradores.length === 0 ? (
              <p style={{ color: '#6B7C74', textAlign: 'center', padding: '24px 0' }}>Sin borradores</p>
            ) : listaBorradores.map(d => {
              const mins = Math.round((Date.now() - d.saved_at) / 60000);
              const hace = mins < 60 ? `hace ${mins} min` : `hace ${Math.round(mins / 60)}h`;
              const expira = Math.round(24 * 60 - mins);
              const esMant = d.tipo === 'mantenimiento';
              return (
                <div key={d.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 14px', borderRadius: 12, marginBottom: 8,
                  background: '#0D1311', border: '1px solid rgba(255,255,255,0.06)',
                }}>
                  <div style={{
                    flexShrink: 0, padding: '3px 8px', borderRadius: 6,
                    fontSize: 11, fontWeight: 700,
                    background: esMant ? '#1A0D00' : '#0F1A12',
                    color: esMant ? '#F59E0B' : '#4CAF50',
                    border: `1px solid ${esMant ? '#F59E0B40' : '#4CAF5040'}`,
                  }}>
                    {esMant ? 'Mant.' : 'Visita'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#F4F7F5',
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {d.ascensor?.comunidad} — {d.ascensor?.label}
                    </div>
                    <div style={{ fontSize: 11, color: '#6B7C74', marginTop: 2 }}>
                      Guardado {hace} · expira en {expira > 0 ? `${expira} min` : 'muy pronto'}
                    </div>
                  </div>
                  <button onClick={() => {
                    setVerBorradores(false);
                    navigate(`/formulario/${d.tipo}`, { state: { ascensor: d.ascensor } });
                  }} style={{
                    background: '#0F6E56', border: 'none', borderRadius: 8,
                    padding: '7px 12px', color: '#fff', fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', flexShrink: 0,
                  }}>
                    Abrir
                  </button>
                  <button onClick={() => {
                    deleteDraft(tecnico.id, d.tipo, d.ascensor_id);
                    recargarBorradores();
                  }} style={{
                    background: '#2D0F0F', border: '1px solid #3D1515', borderRadius: 8,
                    padding: '7px 10px', color: '#EF4444', cursor: 'pointer', flexShrink: 0,
                    display: 'flex', alignItems: 'center',
                  }}>
                    <Trash2 size={15} />
                  </button>
                </div>
              );
            })}
            <div style={{ height: 8 }} />
          </div>
        </div>
      )}
    </div>
  );
}
